import React from 'react';
import { Card } from './ui/card';
import { Shield, Users, TrendingDown, Award, CheckCircle } from 'lucide-react';

const safetyStats = [
  {
    icon: TrendingDown,
    stat: "45%",
    label: "Reduction in Workplace Incidents",
    description: "Companies with proper safety training see dramatic decreases in accidents"
  },
  {
    icon: Users,
    stat: "2.8M",
    label: "Workers Injured Annually",
    description: "Preventable workplace injuries that proper training could avoid"
  },
  {
    icon: Shield,
    stat: "$170B",
    label: "Annual Cost of Injuries",
    description: "Direct and indirect costs businesses face from workplace accidents"
  },
  {
    icon: Award,
    stat: "98%",
    label: "Compliance Success Rate",
    description: "Our trained professionals pass OSHA inspections consistently"
  }
];

const WhySafetyTraining = () => {
  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="container mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-12">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Why It Matters</span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
            Invest in Your Team&apos;s Safety
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Proper OSHA training protects your workforce, reduces costs, and creates a culture of safety
          </p>
        </div>

        {/* Main content grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          {/* Image collage */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              {/* Main large image */}
              <div className="col-span-2 relative rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1653280693244-d72876d5e422?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHx3b3JrZXJzJTIwaGFyZCUyMGhhdHN8ZW58MHx8fHwxNzY4NjkwMjY4fDA&ixlib=rb-4.1.0&q=85&w=800"
                  alt="Construction workers collaborating safely"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="font-semibold">Team Safety First</p>
                  <p className="text-sm text-slate-200">Proper PPE saves lives every day</p>
                </div>
              </div>
              
              {/* Smaller images */}
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://images.unsplash.com/photo-1755548413928-4aaeba7c740e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxzYWZldHklMjB0cmFpbmluZyUyMGNsYXNzcm9vbXxlbnwwfHx8fDE3Njg2OTAyNzR8MA&ixlib=rb-4.1.0&q=85&w=400"
                  alt="Safety training session"
                  className="w-full h-40 object-cover"
                />
              </div>
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://images.unsplash.com/photo-1541888967080-c956a48eaad2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHx3b3JrZXJzJTIwaGFyZCUyMGhhdHN8ZW58MHx8fHwxNzY4NjkwMjY4fDA&ixlib=rb-4.1.0&q=85&w=400"
                  alt="Workers in hard hats"
                  className="w-full h-40 object-cover"
                />
              </div>
            </div>
            
            {/* Floating badge */}
            <div className="absolute -right-4 -bottom-4 bg-emerald-500 text-white p-4 rounded-xl shadow-xl">
              <div className="text-2xl font-bold">500+</div>
              <div className="text-sm">Companies Trust Us</div>
            </div>
          </div>

          {/* Stats and content */}
          <div>
            <div className="space-y-6">
              {safetyStats.map((item, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-slate-900">{item.stat}</span>
                      <span className="text-slate-600 font-medium">{item.label}</span>
                    </div>
                    <p className="text-slate-500 text-sm mt-1">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA within section */}
            <div className="mt-8 p-6 bg-slate-50 rounded-xl border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-2">Ready to protect your team?</h3>
              <p className="text-slate-600 text-sm mb-4">
                Our comprehensive OSHA training covers everything your workforce needs to stay safe and compliant.
              </p>
              <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                {['Hazard Recognition', 'PPE Training', 'Emergency Response', 'Compliance'].map((item, i) => (
                  <span key={i} className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhySafetyTraining;
